import {
  Box,
  Divider,
  Heading,
  HStack,
  IconButton,
  Link,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { PatientDetailsResponse } from '@web-app/graphql/healthie/queries/get-client-details/types';
import { simpleDateFormat } from '@web-app/utils/date-time.utils';
import {
  formatHeight,
  formatLocation,
  formatWeight,
} from '@web-app/utils/format.utils';
import { format } from 'date-fns';
import { ReactNode } from 'react';
import { TbExternalLink } from 'react-icons/tb';

import { CopyButton } from '../../CopyButton';

const NotAvailable = () => (
  <Text fontSize="sm" color="text.subtle" fontStyle="italic">
    (not available)
  </Text>
);

NotAvailable.displayName = 'NotAvailable';

const Stat = ({
  label,
  value,
  copyableValue,
  url,
}: {
  label: string;
  value: ReactNode;
  copyableValue?: string;
  url?: string;
}) => (
  <VStack align="flex-start" gap={0}>
    <Text
      fontWeight={600}
      fontSize="xs"
      color="text.muted"
      textTransform="uppercase"
    >
      {label}
    </Text>
    <Text fontSize="sm" color="text.default">
      {url ? (
        <Link
          href={url}
          isExternal
          color="primary.500"
          textDecoration="underline"
        >
          {value}
        </Link>
      ) : (
        value
      )}
      {copyableValue ? <CopyButton textToCopy={copyableValue} /> : null}
    </Text>
  </VStack>
);

Stat.displayName = 'Stat';

export interface PatientInfoV2UIProps {
  patientData: PatientDetailsResponse;
  appUserData?: {
    hasDownloadedApp: boolean;
    recurringAppointmentsEnabled: boolean;
    healthieId: string | null;
  } | null;
  isAppUserLoading?: boolean;
  isAppUserError?: boolean;
  formattedData: {
    dob?: Date | null;
    height?: number | null;
    weight?: number | null;
    location?: {
      line1?: string | null;
      line2?: string | null;
      city?: string | null;
      state?: string | null;
      zip?: string | null;
      country?: string | null;
    } | null;
    createdAt?: Date | null;
  };
}

/**
 * UI component displaying patient demographic information.
 * This is a pure presentation component that receives all data as props.
 */
export const PatientInfoV2UI: React.FC<PatientInfoV2UIProps> = ({
  patientData,
  appUserData,
  isAppUserLoading = false,
  isAppUserError = false,
  formattedData,
}) => {
  const dateOfBirth = formattedData.dob
    ? format(formattedData.dob, simpleDateFormat)
    : null;

  const height = formattedData.height
    ? formatHeight(formattedData.height)
    : null;

  const weight = formattedData.weight
    ? formatWeight(formattedData.weight)
    : null;
  const location = formattedData.location
    ? formatLocation({
        city: formattedData.location.city ?? null,
        state: formattedData.location.state ?? null,
      })
    : null;

  const clientSince = formattedData.createdAt
    ? format(formattedData.createdAt, simpleDateFormat)
    : null;

  const getMobileAppDisplay = () => {
    if (isAppUserError) {
      return { text: 'Error loading data', color: 'red.500' };
    }
    if (isAppUserLoading) {
      return {
        text: 'Loading...',
        color: 'gray.900',
      };
    }
    if (appUserData) {
      return {
        text: appUserData.hasDownloadedApp ? 'Downloaded' : 'Not Downloaded',
        color: appUserData.hasDownloadedApp ? 'gray.900' : 'red.500',
      };
    }
    return { text: <NotAvailable /> };
  };

  const mobileAppDisplay = getMobileAppDisplay();

  return (
    <Box>
      <Heading as="h2" size="md" mb={4}>
        {patientData?.fullName || <NotAvailable />} &nbsp;
        <Text fontWeight={400} fontSize="sm" color="text.default">
          Healthie ID: {patientData?.id || <NotAvailable />}
          {patientData?.id ? (
            <CopyButton
              textToCopy={patientData.id}
              ml={1}
              copyTooltipLabel="Copy Healthie ID"
            />
          ) : null}
          <Tooltip
            label="Open Healthie case file in new tab"
            fontSize="sm"
            hasArrow
            placement="top"
            borderRadius="md"
          >
            <IconButton
              size="1rem"
              ml={1}
              icon={<TbExternalLink />}
              onClick={() => {
                window.open(
                  `${process.env.NEXT_PUBLIC_HEALTHIE_URL}/users/${patientData.id}`,
                  '_blank',
                );
              }}
              aria-label="Open Healthie Case File"
              variant="ghost"
              color="primary.500"
              _hover={{
                backgroundColor: 'primary.100',
              }}
            />
          </Tooltip>
        </Text>
      </Heading>

      <Divider borderColor="border.default" my={4} />
      <HStack spacing={8} alignItems="flex-start">
        <VStack align="stretch" gap={2} fontSize="sm">
          {/* Clinical Stats */}

          <Stat label="Date of Birth" value={dateOfBirth || <NotAvailable />} />
          <Stat label="Height" value={height || <NotAvailable />} />
          <Stat label="Weight" value={weight || <NotAvailable />} />
          <Stat
            label="Gender"
            value={patientData?.gender || <NotAvailable />}
          />
        </VStack>
        {/* Separator between clinical and non-clinical stats */}
        {/* <Divider borderColor="border.default" /> */}
        <VStack align="stretch" gap={2} fontSize="sm">
          {/* Non-Clinical Stats */}

          <Stat
            label="Phone"
            value={patientData?.phoneNumber || <NotAvailable />}
            copyableValue={patientData?.phoneNumber}
          />
          <Stat label="Location" value={location || <NotAvailable />} />
          <Stat
            label="Mobile App"
            value={
              <Text fontSize="sm" color={mobileAppDisplay.color}>
                {mobileAppDisplay.text}
              </Text>
            }
          />
          <Stat label="Client Since" value={clientSince || <NotAvailable />} />
        </VStack>
      </HStack>
    </Box>
  );
};

PatientInfoV2UI.displayName = 'PatientInfoV2UI';
